import { POSITION_LABELS_PT, type PlayerPosition, type UserPrivate } from '@nossorachao/types';
import { relativeDayLabel } from '@nossorachao/utils';
import { Download, KeyRound, Laptop, Moon, Shield, Smartphone, Sun, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { PageHeader } from '@/components/layout';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PositionPicker,
  SectionTitle,
  Tabs,
  cx,
} from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { useGroups } from '@/lib/queries';
import { useAuth } from '@/store/auth';
import { useTheme } from '@/theme';

type Tab = 'profile' | 'security' | 'privacy';

interface Session {
  id: string;
  device: string | null;
  platform: string | null;
  ipMasked: string | null;
  current: boolean;
  lastUsedAt: string;
}

interface Consent {
  type: string;
  granted: boolean;
  version: string;
}

const CONSENTS: Record<string, { title: string; description: string; locked?: boolean }> = {
  TERMS_OF_USE: {
    title: 'Termos de Uso',
    description: 'Necessário para usar o aplicativo',
    locked: true,
  },
  PRIVACY_POLICY: {
    title: 'Política de Privacidade',
    description: 'Necessário para usar o aplicativo',
    locked: true,
  },
  MARKETING_EMAILS: {
    title: 'E-mails de novidades',
    description: 'Comunicações sobre o produto',
  },
  PUSH_NOTIFICATIONS: {
    title: 'Notificações push',
    description: 'Avisos de pelada, times e mensalidade',
  },
  STATS_SHARING: {
    title: 'Estatísticas no ranking',
    description: 'Exibir seus números no ranking dos grupos',
  },
};

export function ProfilePage() {
  const [tab, setTab] = useState<Tab>('profile');
  const user = useAuth((state) => state.user);
  const { data: groups } = useGroups();

  return (
    <>
      <PageHeader title="Perfil" subtitle={user?.email} />

      <Card className="mb-6">
        <div className="flex items-center gap-4">
          <Avatar name={user?.name ?? '?'} src={user?.avatarUrl} size={64} />
          <div className="min-w-0">
            <h2 className="truncate text-lg font-bold">{user?.name}</h2>
            <p className="truncate text-sm text-ink-3">{user?.email}</p>
            {!user?.emailVerified ? (
              <div className="mt-1.5">
                <Badge tone="warn">E-mail não confirmado</Badge>
              </div>
            ) : null}
          </div>
        </div>

        {groups?.length ? (
          <div className="mt-5 border-t border-line pt-4">
            <p className="mb-2 text-[13px] font-semibold text-ink-2">Meus grupos</p>
            <ul className="space-y-1.5">
              {groups.map((group) => (
                <li key={group.id} className="flex items-center justify-between gap-3">
                  <span className="truncate text-[13px] text-ink-2">{group.name}</span>
                  <Badge tone={group.myRole === 'MEMBER' ? 'neutral' : 'brand'}>
                    {group.myRole === 'OWNER'
                      ? 'Dono'
                      : group.myRole === 'ADMIN'
                        ? 'Admin'
                        : 'Participante'}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Card>

      <div className="mb-5">
        <Tabs<Tab>
          active={tab}
          onChange={setTab}
          tabs={[
            { key: 'profile', label: 'Dados' },
            { key: 'security', label: 'Segurança' },
            { key: 'privacy', label: 'Privacidade' },
          ]}
        />
      </div>

      {tab === 'profile' ? <ProfileTab user={user} /> : null}
      {tab === 'security' ? <SecurityTab /> : null}
      {tab === 'privacy' ? <PrivacyTab /> : null}
    </>
  );
}

// =============================================================================
// Dados
// =============================================================================

function ProfileTab({ user }: { user: UserPrivate | null }) {
  const setUser = useAuth((state) => state.setUser);
  const { theme, setTheme } = useTheme();

  const [name, setName] = useState(user?.name ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [position, setPosition] = useState<PlayerPosition>(user?.position ?? 'MIDFIELDER');
  const [canPlayGoalkeeper, setCanPlayGoalkeeper] = useState(user?.canPlayGoalkeeper ?? false);
  const [feedback, setFeedback] = useState<{ ok: boolean; message: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const isGoalkeeper = position === 'GOALKEEPER';

  /**
   * Trocar para goleiro liga a disposição de ir ao gol automaticamente — a API
   * faria isso de qualquer forma, e ver a caixa marcar sozinha explica a regra
   * melhor que um texto de ajuda.
   */
  function changePosition(next: PlayerPosition) {
    setPosition(next);
    if (next === 'GOALKEEPER') setCanPlayGoalkeeper(true);
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setFeedback(null);

    try {
      const updated = await api.patch<UserPrivate>('/users/me', {
        name: name.trim(),
        ...(phone.trim() ? { phone: phone.trim() } : { phone: null }),
        position,
        canPlayGoalkeeper,
      });
      setUser(updated);
      setFeedback({ ok: true, message: 'Perfil atualizado.' });
    } catch (caught) {
      setFeedback({
        ok: false,
        message: caught instanceof ApiError ? caught.message : 'Não foi possível salvar.',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <form onSubmit={save} className="space-y-4">
          <SectionTitle>Dados pessoais</SectionTitle>

          <Field label="Nome">
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </Field>

          <Field
            label="Celular (WhatsApp)"
            hint="Opcional — usado para os avisos da pelada no seu WhatsApp, quando o grupo usa o bot"
            error={feedback && !feedback.ok ? feedback.message : undefined}
          >
            <Input
              type="tel"
              placeholder="(11) 98765-4321"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </Field>

          <Field label="E-mail" hint="A troca de e-mail exige reverificação e tem fluxo próprio">
            <Input value={user?.email ?? ''} disabled />
          </Field>

          <Field
            label="Posição"
            hint="Usada no sorteio para não deixar um time só de zagueiros contra um só de atacantes"
          >
            <PositionPicker value={position} onChange={changePosition} />
          </Field>

          <label
            className={cx(
              'flex items-start gap-3 rounded-lg border border-line p-3.5',
              isGoalkeeper ? 'opacity-60' : 'cursor-pointer hover:border-line-strong',
            )}
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-[rgb(var(--brand))]"
              checked={canPlayGoalkeeper}
              disabled={isGoalkeeper}
              onChange={(event) => setCanPlayGoalkeeper(event.target.checked)}
            />
            <span className="text-[13px] leading-relaxed">
              <span className="font-semibold">Aceito ir ao gol se faltar goleiro</span>
              <span className="block text-ink-3">
                {isGoalkeeper
                  ? `Quem joga de ${POSITION_LABELS_PT.GOALKEEPER.toLowerCase()} sempre entra nessa lista.`
                  : 'Você continua sendo escalado na linha; só entra no gol quando não sobra ninguém.'}
              </span>
            </span>
          </label>

          {feedback?.ok ? <p className="text-[13px] text-ok">{feedback.message}</p> : null}

          <Button type="submit" loading={saving} disabled={name.trim().length < 2}>
            Salvar
          </Button>
        </form>
      </Card>

      <Card>
        <SectionTitle>Aparência</SectionTitle>
        <div className="mt-4 flex gap-2">
          {[
            { value: 'light' as const, label: 'Claro', icon: Sun },
            { value: 'dark' as const, label: 'Escuro', icon: Moon },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setTheme(option.value)}
              className={cx(
                'flex flex-1 flex-col items-center gap-1.5 rounded-lg border-2 py-4 transition-colors',
                theme === option.value
                  ? 'border-brand bg-brand-soft text-brand'
                  : 'border-line text-ink-2 hover:border-line-strong',
              )}
            >
              <option.icon className="h-5 w-5" aria-hidden />
              <span className="text-[13px] font-semibold">{option.label}</span>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

// =============================================================================
// Segurança
// =============================================================================

function SecurityTab() {
  const logout = useAuth((state) => state.logout);

  const [sessions, setSessions] = useState<Session[]>([]);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [feedback, setFeedback] = useState<{ ok: boolean; message: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void loadSessions();
  }, []);

  async function loadSessions() {
    setSessions(await api.get<Session[]>('/auth/sessions').catch(() => []));
  }

  async function changePassword(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setFeedback(null);

    try {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      setCurrent('');
      setNext('');
      setFeedback({ ok: true, message: 'Senha alterada. As outras sessões foram encerradas.' });
      void loadSessions();
    } catch (caught) {
      setFeedback({
        ok: false,
        message: caught instanceof ApiError ? caught.message : 'Não foi possível alterar.',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <form onSubmit={changePassword} className="space-y-4">
          <SectionTitle>Alterar senha</SectionTitle>

          <Field label="Senha atual">
            <Input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </Field>

          <Field
            label="Nova senha"
            hint="Mínimo de 8 caracteres, com letra e número"
            error={feedback && !feedback.ok ? feedback.message : undefined}
          >
            <Input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(event) => setNext(event.target.value)}
              invalid={feedback ? !feedback.ok : false}
            />
          </Field>

          {feedback?.ok ? <p className="text-[13px] text-ok">{feedback.message}</p> : null}

          <Button
            type="submit"
            icon={KeyRound}
            loading={saving}
            disabled={!current || next.length < 8}
          >
            Alterar senha
          </Button>
        </form>
      </Card>

      <Card className="p-0">
        <div className="p-5 pb-3">
          <SectionTitle>Sessões ativas</SectionTitle>
          <p className="mt-1 text-[13px] text-ink-3">
            O IP aparece mascarado por minimização de dados.
          </p>
        </div>

        <ul className="divide-y divide-line border-t border-line">
          {sessions.map((session) => (
            <li key={session.id} className="flex items-center gap-3 p-4">
              {session.platform === 'ios' || session.platform === 'android' ? (
                <Smartphone className="h-5 w-5 shrink-0 text-ink-3" aria-hidden />
              ) : (
                <Laptop className="h-5 w-5 shrink-0 text-ink-3" aria-hidden />
              )}

              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-[13px]">
                  {session.device ?? 'Dispositivo'}
                  {session.current ? <Badge tone="ok">Este navegador</Badge> : null}
                </p>
                <p className="text-[13px] text-ink-3">
                  {session.ipMasked ?? 'IP não registrado'} ·{' '}
                  {relativeDayLabel(new Date(session.lastUsedAt))}
                </p>
              </div>

              {!session.current ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    await api.delete(`/auth/sessions/${session.id}`).catch(() => undefined);
                    void loadSessions();
                  }}
                >
                  Encerrar
                </Button>
              ) : null}
            </li>
          ))}
        </ul>

        <div className="border-t border-line p-4">
          <Button
            variant="danger"
            full
            onClick={async () => {
              await api.post('/auth/logout-all').catch(() => undefined);
              await logout();
            }}
          >
            Sair de todos os dispositivos
          </Button>
        </div>
      </Card>
    </div>
  );
}

// =============================================================================
// Privacidade
// =============================================================================

function PrivacyTab() {
  const logout = useAuth((state) => state.logout);

  const [consents, setConsents] = useState<Consent[]>([]);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api
      .get<Consent[]>('/users/me/consents')
      .then(setConsents)
      .catch(() => undefined);
  }, []);

  async function toggle(type: string, granted: boolean) {
    const updated = await api
      .patch<Consent[]>('/users/me/consents', { type, granted })
      .catch(() => null);
    if (updated) setConsents(updated);
  }

  async function exportData() {
    const payload = await api.get<unknown>('/users/me/data-export/download');
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `nossorachao-meus-dados-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();

    URL.revokeObjectURL(url);
  }

  async function anonymize() {
    setDeleting(true);
    setError(null);

    try {
      await api.post('/users/me/delete', { password, mode: 'ANONYMIZE' });
      await logout();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Não foi possível concluir.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card className="p-0">
        <div className="p-5 pb-3">
          <SectionTitle>Consentimentos</SectionTitle>
          <p className="mt-1 text-[13px] text-ink-3">
            Você controla o que compartilha e pode revogar quando quiser.
          </p>
        </div>

        <ul className="divide-y divide-line border-t border-line">
          {consents.map((consent) => {
            const meta = CONSENTS[consent.type];
            if (!meta) return null;

            return (
              <li key={consent.type} className="flex items-center justify-between gap-4 p-4">
                <div>
                  <p className="text-sm">{meta.title}</p>
                  <p className="text-[13px] text-ink-3">{meta.description}</p>
                </div>

                <button
                  type="button"
                  role="switch"
                  aria-checked={consent.granted}
                  aria-label={meta.title}
                  disabled={meta.locked}
                  onClick={() => toggle(consent.type, !consent.granted)}
                  className={cx(
                    'relative h-6 w-11 shrink-0 rounded-full transition-colors',
                    consent.granted ? 'bg-brand' : 'bg-line-strong',
                    meta.locked && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <span
                    className={cx(
                      'absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform',
                      consent.granted ? 'translate-x-5' : 'translate-x-0.5',
                    )}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </Card>

      <Card>
        <SectionTitle>Exportar meus dados</SectionTitle>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-2">
          Baixa um arquivo com tudo que guardamos sobre você. As notas que você deu a outros
          jogadores não entram — elas são anônimas e não há como ligá-las a você.
        </p>
        <Button variant="secondary" icon={Download} className="mt-4" onClick={exportData}>
          Baixar meus dados
        </Button>
      </Card>

      <Card accent="rgb(217 45 32)">
        <SectionTitle>
          <span className="flex items-center gap-2 text-bad">
            <Shield className="h-4 w-4" aria-hidden />
            Encerrar minha conta
          </span>
        </SectionTitle>

        <p className="mt-2 text-[13px] leading-relaxed text-ink-2">
          A anonimização apaga seus dados pessoais e preserva o histórico impessoal dos grupos —
          placares e caixa pertencem também às outras pessoas. Se você é dono de algum grupo,
          transfira a propriedade antes.
        </p>

        <div className="mt-4 space-y-3">
          <Field label="Confirme sua senha" error={error ?? undefined}>
            <Input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              invalid={Boolean(error)}
            />
          </Field>

          <Button
            variant="danger"
            icon={Trash2}
            loading={deleting}
            disabled={!password}
            onClick={anonymize}
          >
            Anonimizar minha conta
          </Button>
        </div>
      </Card>
    </div>
  );
}
